import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';

import { dotFormatter, isMobile, resizeContent, roundNumber, setTranslationValues } from '../../../../../helpers';
import { FreeDropDrop, FreeDropPize } from '../../../../../models';
import { useStore, useTranslation } from '../../../../../hooks';
import { FreeDropClaimErrorPopup } from './claim-error-popup';
import { DropGift } from './drop-gift';

interface FreeDropZoneProps {
  minGoal: number;
  prizes: Array<FreeDropPize>;
  nextDrop: FreeDropDrop | null;
}

export const FreeDropZone: React.FC<FreeDropZoneProps> = observer(({ prizes, minGoal }) => {

  const { freeDropStore, gameStore } = useStore();
  const { currencySymbol, precision, isSlotLine } = gameStore;
  const { claimsResponse, freeDropInfo } = freeDropStore;

  const t = useTranslation();

  const [claimErrorMessage, setClaimErrorMessage] = useState('');

  useEffect(() => {
    const errorRes = Object.values(claimsResponse).find((res) => res.data.errorMessage);
    if (errorRes?.data.errorMessage) {
      setClaimErrorMessage(errorRes.data.errorMessage);
    }
  }, [claimsResponse]);

  const dropZoneRefCallback = useCallback((element: HTMLDivElement) => {
    if (element && !isMobile()) {
      resizeContent({ target: element });
      window.addEventListener('resize', () => resizeContent({ target: element }));
    }
  }, []);

  const handleErrorClose = useCallback(() => {
    setClaimErrorMessage('');
    freeDropStore.resetClaimsResponseErrors();
  }, []);

  const claimMessageTranslated = useMemo(() => {
    if (!claimErrorMessage || !freeDropInfo) return '';
    const errorRes = Object.values(claimsResponse).find((res) => res.data.errorMessage);
    return setTranslationValues(t(claimErrorMessage), {
      claimCount: freeDropInfo.claimLimitPerDrop,
      level1TurnoverGoal: `${dotFormatter(roundNumber(minGoal, precision))}${currencySymbol}`,
      currentTurnoverAmount: `${dotFormatter(roundNumber(errorRes?.data?.errorData || 0))}${currencySymbol}`
    });
  }, [claimErrorMessage, claimsResponse, freeDropInfo, minGoal, precision, currencySymbol, t]);

  return (
    <>
      {
        // mobile
        (isMobile() && claimMessageTranslated) ? (
          <FreeDropClaimErrorPopup
            onClose={handleErrorClose}
            message={claimMessageTranslated}
          />
        ) : null
      }
      <div className={`free-drop-zone ${isSlotLine ? 'slot-line' : ''}`} ref={dropZoneRefCallback}>
        {
          // web
          (!isMobile() && claimMessageTranslated) ? (
            <FreeDropClaimErrorPopup
              onClose={handleErrorClose}
              message={claimMessageTranslated}
            />
          ) : null
        }
        {
          prizes.map(({ id, positionX, positionY }) => (
            <DropGift
              id={id}
              key={id}
              positionX={positionX}
              positionY={positionY}
            />
          ))
        }
      </div>
    </>
  );
});
